import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { School, Plus, Edit, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SchoolManagement() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSchool, setShowAddSchool] = useState(false);
  const [newSchool, setNewSchool] = useState({ name: '', address: '', contact_person: '', contact_phone: '' });

  const fetchSchools = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/schools`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchools(response.data);
    } catch (error) {
      toast.error('Failed to load schools');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]);

  const handleAddSchool = async () => {
    try {
      await axios.post(`${API_URL}/schools`, newSchool, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('School added successfully');
      setShowAddSchool(false);
      setNewSchool({ name: '', address: '', contact_person: '', contact_phone: '' });
      fetchSchools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add school');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading schools...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white p-6" data-testid="school-management">
      <div className="container mx-auto max-w-4xl">
        <div className="mb-6">
          <Button variant="outline" onClick={() => navigate('/')} className="rounded-full mb-4" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-heading font-bold" data-testid="page-title">School Management</h1>
              <p className="text-muted-foreground mt-1">Manage schools in the system</p>
            </div>
            <Dialog open={showAddSchool} onOpenChange={setShowAddSchool}>
              <DialogTrigger asChild>
                <Button className="rounded-full" data-testid="add-school-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add School
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="add-school-dialog">
                <DialogHeader>
                  <DialogTitle>Add New School</DialogTitle>
                  <DialogDescription>Fill in the school details below</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">School Name</Label>
                    <Input
                      id="name"
                      value={newSchool.name}
                      onChange={(e) => setNewSchool({ ...newSchool, name: e.target.value })}
                      placeholder="e.g., Springfield High School"
                      data-testid="school-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={newSchool.address}
                      onChange={(e) => setNewSchool({ ...newSchool, address: e.target.value })}
                      placeholder="School address"
                      data-testid="school-address-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_person">Contact Person</Label>
                    <Input
                      id="contact_person"
                      value={newSchool.contact_person}
                      onChange={(e) => setNewSchool({ ...newSchool, contact_person: e.target.value })}
                      placeholder="Principal or contact name"
                      data-testid="school-contact-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_phone">Contact Phone</Label>
                    <Input
                      id="contact_phone"
                      value={newSchool.contact_phone}
                      onChange={(e) => setNewSchool({ ...newSchool, contact_phone: e.target.value })}
                      placeholder="Phone number"
                      data-testid="school-phone-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={handleAddSchool} data-testid="submit-school-button">Add School</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Card className="shadow-lg" data-testid="schools-list">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">Registered Schools</CardTitle>
            <CardDescription>All schools in the NtwanaYam system</CardDescription>
          </CardHeader>
          <CardContent>
            {schools.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="no-schools">
                <School className="w-16 h-16 mx-auto mb-4 text-emerald-300" />
                <p>No schools registered yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {schools.map((school) => (
                  <div
                    key={school.school_id}
                    className="p-4 border rounded-xl hover:shadow-md transition-all"
                    data-testid={`school-item-${school.school_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <School className="w-6 h-6 text-primary" />
                        <div>
                          <h3 className="font-semibold text-lg" data-testid={`school-name-${school.school_id}`}>{school.name}</h3>
                          <p className="text-sm text-muted-foreground" data-testid={`school-address-${school.school_id}`}>{school.address}</p>
                          <p className="text-xs text-muted-foreground mt-1" data-testid={`school-contact-${school.school_id}`}>
                            Contact: {school.contact_person} - {school.contact_phone}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
